import React, { useState } from "react";
import { CSSTransition } from "react-transition-group";
import { connect } from "react-redux";

import { maxLength } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";

import { actions as modelActions } from "features/Model/_ducks";
import { actions as resultsActions } from "features/Model/Review/Results/_ducks";
import { actions as createActions } from "features/CreateModel/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { clearDrawingData } from "features/Model/DrawingNew/_ducks/actions";

import mixpanel from "mixpanel-browser";
import Button from "components/Button";
import Input from "components/Input";
import InputRow from "components/Input/Row";
import { ErrorBar } from "components/AlertBars";

import classes from "../style.module.scss";

const { fetchModelData, modelLoading, setExportErrors, clearModelData } = modelActions;
const { resetResults } = resultsActions;
const { validateMiniAudit, toggleAuditIdValid, setValidationError, createModelFromAudit } = createActions;
const { clearUpgradeData } = upgradeActions;

const MiniAudit = ({
    submitting,
    invalid,
    validateMiniAudit,
    toggleAuditIdValid,
    setValidationError,
    history,
    clearModelData,
    clearDrawingData,
    resetResults,
    clearUpgradeData,
    setExportErrors,
    modelLoading,
    fetchModelData,
    createModelFromAudit,
    hasFiveModels,
}) => {
    const [miniAuditId, setMiniAuditId] = useState("");
    const [modelName, setModelName] = useState("");

    const { uid } = useStateSelector("user");
    const { auditIdIsValid = false, validationError = "", isValidating = false } = useStateSelector("create");

    const handleSubmit = async () => {
        const modelId = await createModelFromAudit({
            miniAuditId,
            uid,
            name: modelName,
        }).then((modelId) => modelId);

        await clearModelData();
        await clearDrawingData();
        await resetResults();
        await clearUpgradeData();
        await setExportErrors([]);
        await modelLoading();
        await fetchModelData(modelId);

        mixpanel.track("Create Model", {});
        return history.push(`/models/${modelId}`);
    };

    return (
        <div className={`${classes.createForm} ${classes.miniAudit}`}>
            <h4 className={classes.step}>1. Validate Assesement Id</h4>
            <InputRow gridTemplate="4fr 1fr">
                <Input
                    name="miniAuditId"
                    type="text"
                    label="Assesement Id"
                    placeholder=""
                    required
                    input={{
                        onChange: (val) => {
                            setMiniAuditId(val);

                            if (auditIdIsValid) {
                                toggleAuditIdValid(false);

                                setModelName("");
                            }

                            setValidationError(null);
                        },
                        value: miniAuditId,
                    }}
                />
                <Button
                    small
                    className={classes.validateButton}
                    onClick={() => validateMiniAudit(miniAuditId)}
                    disabled={!miniAuditId || isValidating}
                >
                    {isValidating ? "Validating..." : "Validate"}
                </Button>
            </InputRow>
            {validationError && !auditIdIsValid && (
                <ErrorBar className={classes.validationError} message={validationError} />
            )}
            <CSSTransition
                in={auditIdIsValid}
                timeout={300}
                classNames={{
                    enter: classes.contentEnter,
                    enterActive: classes.contentEnterActive,
                    enterDone: classes.contentEnterDone,
                }}
            >
                <div className={classes.detailsWrapper}>
                    <h4 className={classes.step}>2. Provide Model Details</h4>
                    <InputRow lastRow>
                        <Input
                            name="name"
                            type="text"
                            label="Name*"
                            placeholder="Name Your Model"
                            required
                            input={{
                                onChange: (val) => setModelName(val),
                                value: modelName,
                            }}
                        />
                    </InputRow>
                </div>
            </CSSTransition>
            <Button
                large
                className={classes.createButton}
                onClick={() => handleSubmit()}
                disabled={submitting || invalid || validationError || !auditIdIsValid || hasFiveModels}
            >
                {submitting ? "Creating Model..." : "Create"}
            </Button>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    validateMiniAudit: (miniAuditId) => dispatch(validateMiniAudit(miniAuditId)),
    toggleAuditIdValid: (valid) => dispatch(toggleAuditIdValid(valid)),
    setValidationError: (error) => dispatch(setValidationError(error)),
    createModelFromAudit: ({ miniAuditId = "", uid = "", name }) =>
        dispatch(createModelFromAudit({ miniAuditId, uid, name })),
    clearModelData: () => dispatch(clearModelData()),
    clearDrawingData: () => dispatch(clearDrawingData()),
    resetResults: () => dispatch(resetResults()),
    clearUpgradeData: () => dispatch(clearUpgradeData()),
    setExportErrors: () => dispatch(setExportErrors()),
    modelLoading: () => dispatch(modelLoading()),
    fetchModelData: (modelId) => dispatch(fetchModelData(modelId)),
});

export default connect(null, mapDispatchToProps)(MiniAudit);
